'use client'
import * as React from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  parseTab,
  useTabsStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { Block } from '@basehub/data/replicache/index'

interface RecentBlockData {
  breadcrumbs?: string[]
  lastOpened: number
  openCount: number
}

type RecentBlock = Block & RecentBlockData

interface RecentBlocksContextType {
  recentBlocks: RecentBlock[]
  clearHistory: () => void
  removeBlock: (blockId: string) => void
}

const RecentBlocksContext = React.createContext<
  RecentBlocksContextType | undefined
>(undefined)

const STORAGE_KEY = 'recentOpenedBlocks'

interface StoredBlockData {
  id: string
  lastOpened: number
  openCount: number
}

class RecentBlocksManager {
  private blocks: Map<string, StoredBlockData>
  private maxItems: number

  constructor(maxItems: number) {
    this.blocks = new Map()
    this.maxItems = maxItems
    this.loadFromStorage()
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const data = JSON.parse(stored) as StoredBlockData[]
        data.forEach((block) => this.blocks.set(block.id, block))
      }
    } catch (error) {
      console.error('Error loading recent blocks:', error)
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(this.blocks.values()))
      )
    } catch (error) {
      console.error('Error saving recent blocks:', error)
    }
  }

  addBlock(blockId: string) {
    const existing = this.blocks.get(blockId)
    const now = Date.now()

    if (existing) {
      this.blocks.set(blockId, {
        ...existing,
        lastOpened: now,
        openCount: existing.openCount + 1,
      })
    } else {
      if (this.blocks.size >= this.maxItems) {
        // Remove oldest block
        const oldestId = Array.from(this.blocks.entries()).sort(
          ([, a], [, b]) => a.lastOpened - b.lastOpened
        )[0]?.[0]
        if (oldestId) this.blocks.delete(oldestId)
      }

      this.blocks.set(blockId, {
        id: blockId,
        lastOpened: now,
        openCount: 1,
      })
    }

    this.saveToStorage()
  }

  removeBlock(blockId: string) {
    this.blocks.delete(blockId)
    this.saveToStorage()
  }

  clear() {
    this.blocks.clear()
    this.saveToStorage()
  }

  getBlocks(): StoredBlockData[] {
    return Array.from(this.blocks.values()).sort(
      (a, b) => b.lastOpened - a.lastOpened
    )
  }
}

export const RecentOpenedTabsProvider: React.FC<{
  children: React.ReactNode
  maxItems?: number
}> = ({ children, maxItems = 100 }) => {
  const [recentBlocks, setRecentBlocks] = React.useState<RecentBlock[]>([])
  const blocksManager = React.useRef(new RecentBlocksManager(maxItems))
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const activeTabKey = useTabsStore((s) => {
    const parsed = s.activeTab && parseTab(s.activeTab)
    return parsed?.type === 'block' || parsed?.type === 'block:diff'
      ? parsed.blockId
      : null
  })

  const updateRecentBlocks = React.useCallback(() => {
    if (!getNode) return

    const storedBlocks = blocksManager.current.getBlocks()
    const validBlocks = storedBlocks
      .map((stored) => {
        const node = getNode(stored.id)
        if (!node?.value) return null

        return {
          ...node.value,
          lastOpened: stored.lastOpened,
          openCount: stored.openCount,
        } as RecentBlock
      })
      .filter((block): block is RecentBlock => Boolean(block))

    setRecentBlocks(validBlocks)
  }, [getNode])

  // Update when active tab changes
  React.useEffect(() => {
    if (!activeTabKey || !getNode) return

    const node = getNode(activeTabKey)
    if (!node?.value) return

    blocksManager.current.addBlock(activeTabKey)
    updateRecentBlocks()
  }, [activeTabKey, getNode, updateRecentBlocks])

  const removeBlock = React.useCallback(
    (blockId: string) => {
      blocksManager.current.removeBlock(blockId)
      updateRecentBlocks()
    },
    [updateRecentBlocks]
  )

  const clearHistory = React.useCallback(() => {
    blocksManager.current.clear()
    updateRecentBlocks()
  }, [updateRecentBlocks])

  const contextValue = React.useMemo(
    () => ({
      recentBlocks,
      clearHistory,
      removeBlock,
    }),
    [recentBlocks, clearHistory, removeBlock]
  )

  return (
    <RecentBlocksContext.Provider value={contextValue}>
      {children}
    </RecentBlocksContext.Provider>
  )
}

export const useRecentOpenedBlocks = () => {
  const context = React.useContext(RecentBlocksContext)
  if (!context) {
    throw new Error(
      'useRecentBlocks must be used within a RecentBlocksProvider'
    )
  }
  return context
}
